import {useCallback} from "react";
import {useBalanceStore} from "../store/useBalance.store";
import useGetUserInfo from "./useGetUserInfo";

/**
 * Hook to check if user needs to register cedula and phone number for pago movil
 * @returns {Object} Object containing needsRegistration boolean and triggerModal function
 */
const usePagoMovilRegistration = () => {
  const {data: userData, isLoading, isSuccess, refetch: refetchUserData} = useGetUserInfo();
  const handleAddPagoMovilModal = useBalanceStore(state => state.handleAddPagoMovilModal);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const setPendingAction = useBalanceStore(state => state.setPendingAction);
  const clearPendingAction = useBalanceStore(state => state.clearPendingAction);
  const pendingAction = useBalanceStore(state => state.pendingAction);

  // Helper to robustly check for empty values
  const isEmpty = val => val === undefined || val === null || val === "" || val === "null";

  // Check if user needs to register cedula and phone number
  const needsRegistration = isSuccess && (isEmpty(userData?.nationalID) || isEmpty(userData?.phoneNumber));

  /**
   * Function to trigger the registration modal if needed for deposits
   * @param {string} actionType - The action type ('deposit' or 'withdrawal')
   * @param {boolean} forceRefresh - Whether to force refresh user data before checking
   * @returns {boolean} true if modal was triggered, false if user is already registered
   */
  const triggerRegistrationModalIfNeeded = async (actionType = "deposit", forceRefresh = true) => {
    if (isLoading || !isSuccess) {
      return false;
    }

    let currentUserData = userData;

    // Always refresh user data to ensure we have the most up-to-date information
    // This prevents stale data issues on first-time checks
    if (forceRefresh) {
      try {
        const refreshResult = await refetchUserData();
        currentUserData = refreshResult?.data?.data?.data;
      } catch (_error) {
        // Fall back to existing userData if refresh fails
        currentUserData = userData;
      }
    }

    // Check if user needs registration with current (refreshed) data
    const needsRegistrationCheck = isEmpty(currentUserData?.nationalID) || isEmpty(currentUserData?.phoneNumber);

    if (needsRegistrationCheck) {
      // Store the pending action so we can continue after registration
      setPendingAction(actionType);
      // Show Add Pago Movil modal with full registration form (personal fields + bank selection)
      handleAddPagoMovilModal();
      return true;
    }

    return false;
  };

  /**
   * Function to execute the pending action after successful registration
   */
  const executePendingAction = useCallback(async () => {
    try {
      // Force refetch user data to ensure we have the latest registration status
      const refreshResult = await refetchUserData();

      // Get the fresh user data from the refetch result
      const freshUserData = refreshResult?.data?.data?.data;

      // Check if user still needs registration with the fresh data
      const stillNeedsRegistration =
        !freshUserData?.nationalID ||
        freshUserData?.nationalID === "" ||
        freshUserData?.nationalID === "null" ||
        !freshUserData?.phoneNumber ||
        freshUserData?.phoneNumber === "" ||
        freshUserData?.phoneNumber === "null";

      if (stillNeedsRegistration) {
        clearPendingAction();
        return;
      }

      // Execute the pending action
      if (pendingAction === "deposit") {
        // Clear the pending action first
        clearPendingAction();
        // Open deposit modal without triggering the verify service
        handleBankAccountModal(true); // Pass true to skip the verify service
      } else if (pendingAction === "withdrawal") {
        // Clear the pending action first
        clearPendingAction();
        // Open withdrawal modal
        handleWithdrawalModal();
      } else {
        clearPendingAction();
      }
    } catch (_error) {
      // Clear pending action even if there's an error
      clearPendingAction();
    }
  }, [pendingAction, clearPendingAction, handleBankAccountModal, handleWithdrawalModal, refetchUserData]);

  return {
    needsRegistration,
    triggerRegistrationModalIfNeeded,
    executePendingAction,
    isUserDataComplete: () => !needsRegistration,
    hasNationalID: !!userData?.nationalID,
    hasPhoneNumber: !!userData?.phoneNumber,
    userData,
    isLoading,
    isDataLoaded: isSuccess,
  };
};

export default usePagoMovilRegistration;
